import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/el";
import "dayjs/locale/en";

import { TUpcomingClassEvent } from "../../../models/ClassEvent";
import { OpenGymLabel } from "../../../components/OpenGymLabel/OpenGymLabel";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import heartAddSvg from "../../../assets/images/icons/heartAdd.svg";
import { ICancelEventParams, useUpcoming } from "./useUpcoming";

import "./Upcoming.scss";
import { ConfirmCancelReservationPopup } from "../../../components/ConfirmCancelReservationPopup/ConfirmCancelReservationPopup";
import { CancelledSuccessfully } from "../../../components/CancelledSuccessfully/CancelledSuccessfully";

dayjs.extend(utc);
dayjs.extend(localizedFormat);

const Upcoming: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedEventForCancel,
    isLoading,
    isLoadingCancel,
    isCancelledSuccessfully,
    upcomingClassEvents,
    onClickGymImage,
    onCancelReservation,
    onConfirmCancelReservationPopup,
    onCloseConfirmCancelReservationPopup,
    onCloseCancelledSuccessfully,
  } = useUpcoming();

  return (
    <div className="upcoming">
      <h1 className="upcoming__title">
        {t("Upcoming")} ({upcomingClassEvents.length || 0})
      </h1>

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {upcomingClassEvents?.length ? (
            <ul className="upcoming__list">
              {upcomingClassEvents.map((event, i) => (
                <React.Fragment key={event.id}>
                  <UpcomingItem
                    event={event}
                    onCancelReservation={onCancelReservation}
                    onClickGymImage={onClickGymImage}
                  />
                  {i !== upcomingClassEvents.length - 1 && <span className="upcoming__divider" />}
                </React.Fragment>
              ))}
            </ul>
          ) : (
            <p className="upcoming__empty">{t("NoUpcomingClassesYet")}</p>
          )}
        </>
      )}

      <ConfirmCancelReservationPopup
        open={!!selectedEventForCancel}
        loading={isLoadingCancel}
        onClose={onCloseConfirmCancelReservationPopup}
        onConfirm={onConfirmCancelReservationPopup}
      />

      <CancelledSuccessfully
        open={!!isCancelledSuccessfully}
        onClose={onCloseCancelledSuccessfully}
      />
    </div>
  );
};

interface UpcomingItemProps {
  event: TUpcomingClassEvent;
  onCancelReservation: (event: ICancelEventParams) => void;
  onClickGymImage: (gymId: string) => void;
}

const UpcomingItem: React.FC<UpcomingItemProps> = ({
  event,
  onCancelReservation,
  onClickGymImage,
}) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === "gr" ? "el" : i18n.language || "en";

  const isOpenGym = event.type !== "GROUP_CLASS";

  const start = useMemo(
    () =>
      dayjs
        .utc(isOpenGym ? event.startDate : event.occuranceDate)
        .local()
        .locale(locale),
    [event, locale, isOpenGym]
  );

  const end = useMemo(
    () => (!isOpenGym ? start.add(event.duration, "minute") : null),
    [start, event, isOpenGym]
  );

  const date = start.format("dddd, D MMM");
  const time = isOpenGym
    ? start.format("h:mm A")
    : `${start.format("h:mm A")} - ${end?.format("h:mm A")}`;

  return (
    <li className="upcoming__item">
      {event.gymListingImage && (
        <img
          className="upcoming__item-image"
          onClick={() => onClickGymImage(event.gymListingId as string)}
          src={event.gymListingImage as string}
          alt={event.gymName}
        />
      )}

      <div className="upcoming__item-content">
        <div className="upcoming__item-header">
          <h4 className="upcoming__item-title">{event.gymName}</h4>
          {isOpenGym && <OpenGymLabel />}
        </div>

        <div className="upcoming__item-info">
          <p className="upcoming__item-text">
            {date} {" | "} {time}
          </p>
          <p className="upcoming__item-text">{event.address}</p>
          <p className="upcoming__item-text">
            {event.creditsValue} {t("Credits")}
          </p>
        </div>

        <div className="upcoming__item-footer">
          {/* <img
            className="upcoming__item-favorite"
            src={heartAddSvg}
            width={28}
            height={28}
            alt="Add gym to saved"
          /> */}

          {event.canBeCancelled && (
            <CustomButton
              text={t("CancelReservation")}
              type="button"
              typeStyle="fill"
              style={{
                width: "200px",
              }}
              functionOnClick={() =>
                onCancelReservation({
                  id: event.id,
                  gymListingId: event.gymListingId as string,
                  type: event.type,
                })
              }
            />
          )}
        </div>
      </div>
    </li>
  );
};

const Spinner: React.FC = () => (
  <div className="upcoming__spinner">
    <Spin size="large" />
  </div>
);

export default Upcoming;
